import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDate } from "@/lib/format";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = { doc_type: "FC", old_expiry: "", new_expiry: "", notes: "" };

export default function VehicleRenewalDialog({ vehicle, onClose }) {
  const [logs, setLogs] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!vehicle) return;
    api.get(`/vehicles/${vehicle.id}/renewals`).then((r) => setLogs(r.data || [])).catch(() => setLogs([]));
  }, [vehicle]);

  useEffect(() => {
    setLogs(null);
    setForm(EMPTY_FORM);
    load();
  }, [load]);

  const addLog = async () => {
    if (!form.new_expiry && !form.old_expiry) {
      toast.error("Enter at least one expiry date");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/vehicles/${vehicle.id}/renewals`, form);
      toast.success("Renewal entry added");
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deleteLog = async (id) => {
    try {
      await api.delete(`/vehicle-renewals/${id}`);
      toast.success("Entry deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={!!vehicle} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[720px]" data-testid="vehicle-renewal-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Doc Renewal Log — {vehicle?.registration_no || ""}</DialogTitle>
        </DialogHeader>

        <div className="rounded-md border border-border bg-slate-50/60 p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Add manual entry</div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="grid gap-1.5">
              <Label className="text-xs">Document</Label>
              <Select value={form.doc_type} onValueChange={(v) => setForm((f) => ({ ...f, doc_type: v }))}>
                <SelectTrigger data-testid="renewal-doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="FC">FC</SelectItem>
                  <SelectItem value="Insurance">Insurance</SelectItem>
                  <SelectItem value="Permit">Permit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Old expiry</Label>
              <Input type="date" value={form.old_expiry} onChange={(e) => setForm((f) => ({ ...f, old_expiry: e.target.value }))} data-testid="renewal-old-expiry-input" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">New expiry</Label>
              <Input type="date" value={form.new_expiry} onChange={(e) => setForm((f) => ({ ...f, new_expiry: e.target.value }))} data-testid="renewal-new-expiry-input" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Optional" data-testid="renewal-notes-input" />
            </div>
          </div>
          <Button size="sm" className="mt-3" onClick={addLog} disabled={saving} data-testid="add-renewal-btn">
            <Plus className="mr-1 h-4 w-4" /> {saving ? "Adding…" : "Add Entry"}
          </Button>
        </div>

        {logs === null ? (
          <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>
        ) : logs.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground" data-testid="renewal-empty-state">
            No renewal history yet. Old expiry dates are logged automatically whenever you update FC / Insurance / Permit dates.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Logged On</TableHead>
                <TableHead>Document</TableHead>
                <TableHead>Old Expiry</TableHead>
                <TableHead>New Expiry</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((l) => (
                <TableRow key={l.id} data-testid="renewal-log-row">
                  <TableCell className="text-sm">{fmtDate(String(l.created_at || "").slice(0, 10))}</TableCell>
                  <TableCell className="text-sm font-medium">{l.doc_type}</TableCell>
                  <TableCell className="text-sm">{l.old_expiry ? fmtDate(l.old_expiry) : "—"}</TableCell>
                  <TableCell className="text-sm">{l.new_expiry ? fmtDate(l.new_expiry) : "—"}</TableCell>
                  <TableCell>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${l.source === "auto" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"}`}>
                      {l.source === "auto" ? "Auto" : "Manual"}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{l.notes || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => deleteLog(l.id)} data-testid="delete-renewal-btn">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
